import { NgClass } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { SocialChallengeBloc } from '@kf-sc';
import { DailyChallengeStatusBloc } from '@kp/purposeful-day/daily-challenge-status.bloc';
import { ModalPurposefulDayComponent } from '@kp/purposeful-day/modal-purposeful-day/modal-purposeful-day.component';
import { PurposefulDay } from '@kp/purposeful-day/purposeful-day.model';
import { StreakBloc } from '@kp/streaks/streak.bloc';
import { UiModule } from '@kui';
import { MockComponent } from '@kutil/test';
import { SvgIconComponent } from 'angular-svg-icon';
import { Subject, combineLatestWith } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-purposeful-day-indicator',
  standalone: true,
  templateUrl: './purposeful-day-indicator.component.html',
  styleUrls: ['./purposeful-day-indicator.component.scss'],
  imports: [NgClass, SvgIconComponent, UiModule, ModalPurposefulDayComponent, RouterLink, TranslocoDirective],
})
export class PurposefulDayIndicatorComponent implements OnInit, OnDestroy {
  constructor(
    private _dailyChallengeStatusBloc: DailyChallengeStatusBloc,
    private _socialChallengeBloc: SocialChallengeBloc,
    private _streakBloc: StreakBloc
  ) {}

  @Output() purposefulDaysClicked = new EventEmitter<void>();

  complete = false;
  purposefulDayStatus: PurposefulDay;
  showCelebrationModal = false;
  streakLength = 0;

  private _destroy$ = new Subject<void>();
  private _previouslyCompleted: boolean;

  ngOnInit() {
    this._dailyChallengeStatusBloc.shownPurposefulDayCelebration$
      .pipe(takeUntil(this._destroy$))
      .subscribe((shownPurposefulDayCelebration) => {
        this._previouslyCompleted = shownPurposefulDayCelebration;
      });
    this._dailyChallengeStatusBloc.purposefulDayStatus$
      .pipe(combineLatestWith(this._socialChallengeBloc.showCelebrationModal$), takeUntil(this._destroy$))
      .subscribe(([dailyChallengeStatus, showSocialChallengeCelebrationModal]) => {
        const status = dailyChallengeStatus?.data;
        if (status) {
          this._updateStatus(status, showSocialChallengeCelebrationModal);
        }
      });
    this._dailyChallengeStatusBloc.refreshPurposefulDayStatus();

    this._streakBloc.streak$.pipe(takeUntil(this._destroy$)).subscribe((response) => {
      const streaks = response?.data;
      if (streaks) {
        this.streakLength = streaks.current || 0;
      }
    });
    this._streakBloc.refreshStreaks();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  isCelebrationModalOpen() {
    this.showCelebrationModal = false;
  }

  private _updateStatus(purposefulDayStatus: PurposefulDay, showSocialChallengeCelebrationModal: boolean) {
    this.purposefulDayStatus = purposefulDayStatus;
    this.complete = purposefulDayStatus.purposefulDayCompleted();

    // If the purposeful day steps were not previously completed, but are now then present the celebration modal
    if (this._previouslyCompleted != null && !this._previouslyCompleted && this.complete) {
      this._showCelebrationModal(showSocialChallengeCelebrationModal);
    }

    // Update the current purposeful day steps completion status
    this._previouslyCompleted = this.complete;

    this._dailyChallengeStatusBloc.setPurposefulDayCelebrationStatus(this._previouslyCompleted);
  }

  private _showCelebrationModal(showSocialChallengeCelebrationModal: boolean) {
    // only show Purposeful Day celebration if social challenge is not showing
    this.showCelebrationModal = !showSocialChallengeCelebrationModal;
  }
}

export const MockPurposefulDayIndicatorComponent = MockComponent({
  selector: 'kp-purposeful-day-indicator',
  template: '<ng-content></ng-content>',
});
